import { ReactNode } from 'react';

/**
 * PageHeader — the canonical list/report page header: an <h1> title (with an
 * optional subtitle/count line) on the left and a right-aligned action-button
 * cluster (Columns / Refresh / Export / + New …).
 *
 * Why this exists (mobile-responsive tightening): list and report pages each
 * rolled their own `<header style={{ display:'flex', justifyContent:'space-
 * between' }}>` with a non-wrapping action `<div>`. On a ~390–430px viewport
 * (iPhone 13 Pro Max) the action cluster overflowed horizontally and the
 * primary button was clipped off the right edge. Centralizing the layout here
 * makes the fix systemic:
 *   - the whole header `flex-wrap`s, so the action cluster drops to a second
 *     line when the title + buttons can't share one row;
 *   - the action cluster itself `flex-wrap`s and stays right-aligned, so no
 *     button is ever clipped;
 *   - the title block can shrink (`minWidth: 0`) so a long title truncates
 *     rather than shoving the buttons off-canvas.
 *
 * Desktop is unchanged: at ≥ the width where everything fits on one row, wrap
 * never triggers and the layout is identical to the previous bespoke headers.
 *
 * Token discipline: title → --fs-3xl / --color-ink; subtitle → --color-ink-3.
 * Purely presentational — matches the visual the list pages already shipped.
 */

export interface PageHeaderProps {
  title: ReactNode;
  /** Optional supporting line under the title (e.g. "1,204 parcels"). */
  subtitle?: ReactNode;
  /** Right-aligned action-button cluster. */
  actions?: ReactNode;
}

export function PageHeader({ title, subtitle, actions }: PageHeaderProps) {
  return (
    <header style={{
      marginBottom: 'var(--sp-5)',
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'space-between',
      gap: 'var(--sp-3)',
      flexWrap: 'wrap',
    }}>
      <div style={{ minWidth: 0, flex: '1 1 auto' }}>
        <h1 style={{
          margin: 0,
          fontSize: 'var(--fs-3xl)',
          fontWeight: 'var(--fw-semibold)',
          letterSpacing: 'var(--ls-tight)',
          color: 'var(--color-ink)',
        }}>{title}</h1>
        {subtitle != null && subtitle !== false && (
          <p style={{
            margin: 'var(--sp-1) 0 0',
            color: 'var(--color-ink-3)',
            fontSize: 'var(--fs-md)',
          }}>{subtitle}</p>
        )}
      </div>
      {actions && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-2)',
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
        }}>{actions}</div>
      )}
    </header>
  );
}
